// services/storiesApi.ts — HTTP client for /api/stories/*.
//
// Endpoints, request shapes and response shapes match STORIES_CONTRACT.md §5
// exactly. All endpoints are bearer-authed via authedFetch (handles 401 →
// refresh → retry once).
//
// Errors are surfaced as `StoriesApiError` (or one of its subclasses) so
// callers can do a single `instanceof StoriesApiError` check and then
// narrow to the specific subclass for the contractual error codes:
//   - 402 story_cap_reached       → StoryCapReachedError (carries `limit`)
//   - 402 subscription_required   → SubscriptionRequiredError
//   - 403 scenario_locked         → ScenarioLockedError (legacy — replaced
//                                    by subscription_required under the
//                                    forever-default lock; kept for back-
//                                    compat in case any server still emits)
//   - 409 story_finished          → StoryFinishedError
// Any other non-2xx becomes a plain StoriesApiError carrying status/code/body.

import { authedFetch } from "../auth/tokens";
import type { GameState } from "../engine";

// ── Types — match STORIES_CONTRACT §5 exactly ───────────────────────────

export type StoryStatus = "active" | "finished";

export interface StoryListEntry {
  id: string;
  name: string;
  scenarioId: string;
  status: StoryStatus;
  /** ISO timestamp from the server. */
  createdAt: string;
  /** ISO timestamp from the server. */
  lastPlayedAt: string;
  /**
   * Per-story lock state under the forever-default model
   * (STORIES_CONTRACT §1). True when this story's scenarioId differs from
   * the caller's `users.forever_default_scenario_id` AND the caller is on
   * the free tier with no overriding tester flag. Read-only access is
   * unconditional; this flag drives whether the client renders the
   * in-game paywall overlay / locked badge.
   *
   * Always provided by the server. Default false on legacy payloads from
   * a server predating the flag, so client logic that branches on it
   * stays safe.
   */
  requiresSubscriptionToWrite: boolean;
  /**
   * Phase A i18n — narration language locked at story creation (BCP-47
   * code, e.g. "fr", "es", "zh-Hans"). Default "en" on the column so
   * legacy rows always carry a value. Optional on the type for back-
   * compat with old server bundles; treat absence as "en".
   */
  language?: string;
}

export interface StoryFullEntry extends StoryListEntry {
  state: GameState;
}

export interface StoriesResponse {
  stories: StoryListEntry[];
  activeStoryId: string | null;
}

// ── Errors ──────────────────────────────────────────────────────────────

/**
 * Base error for every non-2xx response from /api/stories/*.
 * `code` and `body` come from the server's `{ error, code, ... }` envelope
 * when present; `status` is always set.
 */
export class StoriesApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: unknown;
  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = "StoriesApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** 402 — tier cap reached on POST /api/stories. Includes the limit. */
export class StoryCapReachedError extends StoriesApiError {
  readonly limit: number;
  constructor(limit: number, body?: unknown) {
    super("story_cap_reached", 402, "story_cap_reached", body);
    this.name = "StoryCapReachedError";
    this.limit = limit;
  }
}

/**
 * 403 — free user tried to create a paid scenario under the legacy free-tier
 * model. Replaced by {@link SubscriptionRequiredError} under the
 * forever-default lock; kept as a subclass for back-compat in case any
 * older server build is still in front of us.
 */
export class ScenarioLockedError extends StoriesApiError {
  constructor(body?: unknown) {
    super("scenario_locked", 403, "scenario_locked", body);
    this.name = "ScenarioLockedError";
  }
}

/**
 * 402 — free user tried to write to a story whose scenario doesn't match
 * their `users.forever_default_scenario_id`. The web client should open
 * the Paywall with `reason="story_locked"`. Optional `scenarioId` is
 * stamped by the server on the create path so the client can show the
 * exact scenario in the paywall copy if it wants to.
 */
export class SubscriptionRequiredError extends StoriesApiError {
  readonly scenarioId: string | null;
  constructor(scenarioId: string | null, body?: unknown) {
    super("subscription_required", 402, "subscription_required", body);
    this.name = "SubscriptionRequiredError";
    this.scenarioId = scenarioId;
  }
}

/** 409 — PUT against a finalised story. */
export class StoryFinishedError extends StoriesApiError {
  constructor(body?: unknown) {
    super("story_finished", 409, "story_finished", body);
    this.name = "StoryFinishedError";
  }
}

// ── Helper: parse response, throw a StoriesApiError on non-2xx ──────────
// Mirrors the error-body-stashing behaviour of authApi.ts / billingApi.ts.

interface ServerErrBody {
  error?: string;
  code?: string;
  limit?: number;
  scenarioId?: string;
}

async function parseStoryResponse<T>(res: Response): Promise<T> {
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — fall through.
    }
  }
  if (!res.ok) {
    const errBody = (body as ServerErrBody | null) ?? {};
    const code = errBody.code;
    const message =
      errBody.error ||
      (text ? text.slice(0, 200) : `Request failed with status ${res.status}`);

    // Map the contractual codes to subclasses; everything else is a plain
    // StoriesApiError. Code-first then status-first so the server can use
    // any HTTP status as long as the code is correct.
    if (res.status === 402 && code === "story_cap_reached") {
      const limit =
        typeof errBody.limit === "number" && Number.isFinite(errBody.limit)
          ? errBody.limit
          : 0;
      throw new StoryCapReachedError(limit, body);
    }
    if (code === "subscription_required") {
      const sid =
        typeof errBody.scenarioId === "string" ? errBody.scenarioId : null;
      throw new SubscriptionRequiredError(sid, body);
    }
    if (res.status === 403 && code === "scenario_locked") {
      throw new ScenarioLockedError(body);
    }
    if (res.status === 409 && code === "story_finished") {
      throw new StoryFinishedError(body);
    }
    throw new StoriesApiError(message, res.status, code, body);
  }
  return body as T;
}

// ── Endpoints ───────────────────────────────────────────────────────────

/**
 * GET /api/stories — list the caller's stories (no full state on each row).
 * Includes the user's currently active story id so the UI can highlight it.
 */
export async function listStories(): Promise<StoriesResponse> {
  const res = await authedFetch(`/api/stories`, { method: "GET" });
  return parseStoryResponse<StoriesResponse>(res);
}

/**
 * POST /api/stories — create a new story for the given scenario.
 * `name` is optional; the server falls back to "Untitled" when omitted.
 *
 * Throws:
 *   - StoryCapReachedError on 402 (tier cap)
 *   - SubscriptionRequiredError on 402 (free-tier forever-default lock —
 *     the user picked a scenario that isn't their forever_default and
 *     they don't have an active subscription)
 *   - ScenarioLockedError on 403 (legacy free-tier scenario lock — older
 *     server builds; the new server returns subscription_required)
 *   - StoriesApiError on any other non-2xx
 */
export async function createStory(
  scenarioId: string,
  name?: string,
  language?: string,
): Promise<{ story: StoryFullEntry }> {
  // Only include `name` / `language` in the body when defined so the
  // server defaults kick in for the absent path:
  //   - name absent → "Untitled"
  //   - language absent → users.language → "en"
  // Sending an explicit value is what triggers the per-story override
  // path; absence is the "use my account default" path.
  const payload: { scenarioId: string; name?: string; language?: string } = {
    scenarioId,
  };
  if (name !== undefined) payload.name = name;
  if (language !== undefined) payload.language = language;

  const res = await authedFetch(`/api/stories`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return parseStoryResponse<{ story: StoryFullEntry }>(res);
}

/**
 * GET /api/stories/:id — load a single story including its full GameState.
 */
export async function getStory(id: string): Promise<{ story: StoryFullEntry }> {
  const res = await authedFetch(`/api/stories/${encodeURIComponent(id)}`, {
    method: "GET",
  });
  return parseStoryResponse<{ story: StoryFullEntry }>(res);
}

/**
 * PUT /api/stories/:id — overwrite the saved state for a story.
 *
 * Throws:
 *   - StoryFinishedError on 409 (the story has been finalised)
 *   - SubscriptionRequiredError on 402 (the story is locked under the
 *     caller's forever-default — they need to subscribe to write to it)
 */
export async function putStory(
  id: string,
  state: GameState,
): Promise<{ ok: true; lastPlayedAt: string }> {
  const res = await authedFetch(`/api/stories/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify({ state }),
  });
  return parseStoryResponse<{ ok: true; lastPlayedAt: string }>(res);
}

/**
 * DELETE /api/stories/:id — remove a story.
 * Server is idempotent — returns 200 even if the row didn't exist.
 */
export async function deleteStory(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(`/api/stories/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  return parseStoryResponse<{ ok: true }>(res);
}

/**
 * POST /api/stories/:id/activate — set this story as the user's active one.
 * /api/turn (and the legacy /api/save alias) operate on the active story.
 */
export async function activateStory(id: string): Promise<{ ok: true }> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(id)}/activate`,
    { method: "POST" },
  );
  return parseStoryResponse<{ ok: true }>(res);
}

/**
 * POST /api/stories/:id/restart — reset this story to a fresh scenario
 * opening. The server preserves the account-level player identity
 * (gender / drawnTo / name) so the player doesn't re-do CharacterSetup.
 *
 * Throws StoryFinishedError on 409 (the story has been finalised) so the
 * caller can handle it the same way as a finalised PUT, or
 * SubscriptionRequiredError on 402 when the story is locked under the
 * caller's forever-default.
 */
export async function restartStory(
  id: string,
): Promise<{ story: StoryFullEntry }> {
  const res = await authedFetch(
    `/api/stories/${encodeURIComponent(id)}/restart`,
    { method: "POST" },
  );
  return parseStoryResponse<{ story: StoryFullEntry }>(res);
}

/**
 * PATCH /api/stories/:id — rename a story. The name is trimmed server-side
 * and validated to 1..120 chars; pass a non-empty string.
 *
 * Used by the post-CharacterSetup NamePrompt flow — stories are created
 * with a placeholder ("Untitled") at scenario-pick time and renamed once
 * the player has had a chance to name them.
 *
 * Throws StoryFinishedError on 409 (the story has been finalised) so the
 * caller can treat it the same way as a finalised PUT / restart.
 */
export async function renameStory(
  id: string,
  name: string,
): Promise<{ story: StoryListEntry }> {
  const res = await authedFetch(`/api/stories/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
  return parseStoryResponse<{ story: StoryListEntry }>(res);
}
